/**
 * Display component for session code with ABC-123-XYZ formatting.
 */

import { useTranslation } from 'react-i18next'

interface SessionCodeProps {
  /** Raw 9-character code (e.g., "ABC123XYZ") */
  code: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Additional CSS classes */
  className?: string
}

const sizeClasses = {
  sm: 'text-lg',
  md: 'text-2xl',
  lg: 'text-4xl',
}

/**
 * Format raw code as ABC-123-XYZ for display.
 */
function formatCode(code: string): string {
  const clean = code.replace(/-/g, '').toUpperCase()
  const parts: string[] = []
  
  for (let i = 0; i < clean.length && i < 9; i += 3) {
    parts.push(clean.slice(i, i + 3))
  }
  
  return parts.join('-')
}

export function SessionCode({ code, size = 'md', className = '' }: SessionCodeProps) {
  const { t } = useTranslation()
  const formatted = formatCode(code)
  
  return (
    <span 
      // font-mono, never the pixel display face: a 9-character code is read
      // glyph by glyph and retyped, and the pixel face blurs at these sizes.
      className={`font-mono font-bold tracking-wider ${sizeClasses[size]} ${className}`}
      aria-label={t('join.sessionCodeAriaLabelValue', { code: formatted })}
    >
      {formatted}
    </span>
  )
}
